import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "../auth/AuthContext";
import {
  api,
  ApiError,
  type RankingStandingRow,
  type RankingSystemSummary,
} from "../lib/api";

/** Panel de sistemas de ranking (R2): crear el sistema desde el preset FMT,
 * ver standings vivos por categoría y PUBLICAR el snapshot congelado con el
 * que se arman los draws. Operación de plataforma (superadmin). */
export default function AdminRankingSystemsPage() {
  const { token } = useAuth();
  const [systems, setSystems] = useState<RankingSystemSummary[]>([]);
  const [selectedSystemId, setSelectedSystemId] = useState<number | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [standings, setStandings] = useState<RankingStandingRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await api.rankings.listSystems(token);
      setSystems(resp.data.systems);
      if (resp.data.systems.length > 0 && selectedSystemId === null) {
        setSelectedSystemId(resp.data.systems[0].id);
        setSelectedCategoryId(resp.data.systems[0].categories[0]?.id ?? null);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    }
  }, [token, selectedSystemId]);

  useEffect(() => {
    void load();
  }, [load]);

  const system = systems.find((item) => item.id === selectedSystemId) ?? null;

  const loadStandings = useCallback(async () => {
    if (!token || !selectedSystemId || !selectedCategoryId) {
      setStandings([]);
      return;
    }
    try {
      const resp = await api.rankings.standings(token, selectedSystemId, selectedCategoryId);
      setStandings(resp.data.standings);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    }
  }, [token, selectedSystemId, selectedCategoryId]);

  useEffect(() => {
    void loadStandings();
  }, [loadStandings]);

  async function handleCreateFmt() {
    if (!token) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await api.rankings.createSystem(token, { preset_slug: "fmt-cnij-2026" });
      setMessage("Sistema FMT CNIJ-2026 creado con sus 4 categorías y tablas del reglamento.");
      setSelectedSystemId(null);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  async function handlePublish() {
    if (!token || !selectedSystemId || !selectedCategoryId) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const resp = await api.rankings.publish(token, selectedSystemId, {
        age_category_id: selectedCategoryId,
      });
      setMessage(
        `Publicación #${resp.data.publication_id} congelada con ${resp.data.rows.length} jugador(es). Con esta se arman aceptación y siembra.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="admin-ranking-systems">
      <div className="card">
        <p className="eyebrow">Plataforma</p>
        <h1>Sistemas de ranking</h1>
        <p className="muted">
          Un sistema por circuito (p.ej. el ranking nacional FMT). Los torneos se ligan desde su paso
          de Ranking ("Puntúa para…"); los resultados generan puntos solos y aquí se publica el
          snapshot oficial.
        </p>

        {message && <p className="form-message">{message}</p>}
        {error && <p className="form-error" role="alert">{error}</p>}

        {systems.length === 0 ? (
          <div className="admin-ranking-systems__empty">
            <p className="muted">Aún no hay sistemas de ranking.</p>
            <button type="button" className="btn" onClick={() => void handleCreateFmt()} disabled={busy}>
              {busy ? "Creando…" : "Crear ranking FMT CNIJ-2026"}
            </button>
          </div>
        ) : (
          <div className="admin-ranking-systems__controls">
            <label>
              Sistema
              <select
                value={selectedSystemId ?? ""}
                onChange={(e) => {
                  const id = Number(e.target.value);
                  setSelectedSystemId(id);
                  const next = systems.find((item) => item.id === id);
                  setSelectedCategoryId(next?.categories[0]?.id ?? null);
                }}
              >
                {systems.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </label>
            <label>
              Categoría
              <select
                value={selectedCategoryId ?? ""}
                onChange={(e) => setSelectedCategoryId(Number(e.target.value))}
              >
                {(system?.categories ?? []).map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name} · mejores {category.best_n}
                  </option>
                ))}
              </select>
            </label>
            <button type="button" className="btn" onClick={() => void handlePublish()} disabled={busy || standings.length === 0}>
              {busy ? "Publicando…" : "Publicar ranking"}
            </button>
            {system && (
              <Link className="btn btn--ghost" to={`/rankings/${system.slug}`}>
                Ver página pública
              </Link>
            )}
          </div>
        )}
      </div>

      {system && (
        <div className="card">
          <h2>Standings vivos · {system.categories.find((c) => c.id === selectedCategoryId)?.name ?? ""}</h2>
          {standings.length === 0 ? (
            <p className="muted">
              Sin puntos aún en esta categoría. Liga torneos desde su paso de Ranking y captura resultados.
            </p>
          ) : (
            <div className="admin-ranking-systems__table-wrap">
              <table className="admin-ranking-systems__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Jugador</th>
                    <th>Total</th>
                    <th>Singles</th>
                    <th>Dobles (20%)</th>
                    <th>Torneos</th>
                  </tr>
                </thead>
                <tbody>
                  {standings.map((row) => (
                    <tr key={row.player_profile_id}>
                      <td>{row.position}</td>
                      <td>{row.display_name}</td>
                      <td><strong>{row.points_total.toLocaleString("es-MX")}</strong></td>
                      <td>{row.points_singles.toLocaleString("es-MX")}</td>
                      <td>{row.points_doubles_counted.toLocaleString("es-MX")}</td>
                      <td>{row.tournaments_counted}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
